import React from 'react';
import { FlaskConical, TrendingUp, TrendingDown, Eye, Users2 } from 'lucide-react';
import { useSuperAdminContext } from '../SuperAdminContext';

const fmt = (n) => (n ?? 0).toLocaleString();

function ConversionWidget({ data }) {
  if (!data) return null;
  const { main = {}, peptide = {}, window_days } = data;

  const peptideBetter = peptide.conversion_rate > main.conversion_rate;
  const delta =
    (peptide.conversion_rate || 0) - (main.conversion_rate || 0);

  return (
    <div
      data-testid="landing-conversion-widget"
      style={{
        background:
          'linear-gradient(135deg, rgba(74,222,128,0.06) 0%, rgba(59,130,246,0.05) 100%)',
        border: '1px solid rgba(74,222,128,0.2)',
        borderRadius: '14px',
        padding: '24px',
        marginTop: '24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '18px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <h3
          style={{
            margin: 0,
            color: '#e4e6eb',
            fontSize: '1.05rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <FlaskConical size={18} style={{ color: '#4ade80' }} />
          Landing Conversion — Peptide vs. Main
        </h3>
        <span
          data-testid="landing-conversion-window"
          style={{
            fontSize: '0.8rem',
            color: '#9ca3af',
            background: 'rgba(255,255,255,0.04)',
            padding: '4px 12px',
            borderRadius: '999px',
          }}
        >
          Last {window_days ?? 30} days
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
        }}
      >
        <div
          data-testid="landing-conversion-main-card"
          style={{
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.2)',
            borderRadius: '12px',
            padding: '18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#60a5fa',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: '10px',
            }}
          >
            <Users2 size={14} /> MAIN LANDING ( / )
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#e4e6eb' }}>
            {main.conversion_rate || 0}%
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '6px' }}>
            <Eye size={12} style={{ display: 'inline', marginRight: '4px' }} />
            {fmt(main.visits)} visits · {fmt(main.leads)} leads
          </div>
        </div>

        <div
          data-testid="landing-conversion-peptide-card"
          style={{
            background: 'rgba(74,222,128,0.08)',
            border: '1px solid rgba(74,222,128,0.25)',
            borderRadius: '12px',
            padding: '18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#4ade80',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: '10px',
            }}
          >
            <FlaskConical size={14} /> PEPTIDE LANDING ( /peptides )
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#e4e6eb' }}>
            {peptide.conversion_rate || 0}%
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '6px' }}>
            <Eye size={12} style={{ display: 'inline', marginRight: '4px' }} />
            {fmt(peptide.visits)} visits · {fmt(peptide.leads)} leads
          </div>
        </div>

        <div
          data-testid="landing-conversion-delta-card"
          style={{
            background: peptideBetter
              ? 'rgba(74,222,128,0.05)'
              : 'rgba(251,146,60,0.05)',
            border: `1px solid ${
              peptideBetter ? 'rgba(74,222,128,0.2)' : 'rgba(251,146,60,0.2)'
            }`,
            borderRadius: '12px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              color: '#9ca3af',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              marginBottom: '8px',
            }}
          >
            PEPTIDE DELTA
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: peptideBetter ? '#4ade80' : '#fb923c',
              fontSize: '1.4rem',
              fontWeight: 700,
            }}
          >
            {peptideBetter ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
            {delta >= 0 ? '+' : ''}
            {delta.toFixed(2)} pts
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '6px' }}>
            {peptideBetter
              ? 'Peptide page is converting better.'
              : main.visits === 0
              ? 'Not enough main-landing data yet.'
              : 'Main page is converting better.'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OverviewTab() {
  const ctx = useSuperAdminContext();
  const { activeTab, setActiveTab, stats, landingConversion } = ctx;

  if (activeTab !== 'overview') return null;

  return (
    <div className="overview-content" data-testid="overview-tab">
      <h2>Platform Overview</h2>
      <p>
        Welcome to the Super Admin Dashboard. You have complete access to all
        platform data and controls.
      </p>

      {stats && (
        <div className="overview-sections">
          <div className="overview-section">
            <h3>Quick Actions</h3>
            <button
              className="btn-primary"
              onClick={() => setActiveTab('inquiries')}
            >
              View New Inquiries ({stats.inquiries.new})
            </button>
            <button
              className="btn-primary"
              onClick={() => setActiveTab('automation')}
            >
              Check Automation Status
            </button>
          </div>
        </div>
      )}

      <ConversionWidget data={landingConversion} />
    </div>
  );
}
